<template>
    <!-- Простые категории без дочерних -->
    <div v-if="simpleCategories.length > 0" class="mb-6">
        <div class="viewers-list flex flex-wrap gap-3">
            <div 
                v-for="category in simpleCategories" 
                :key="category._id"
                class="document"
                @mouseover="hover(category._id)" 
                @mouseleave="leave"
            >
                <router-link 
                    :to="category.images_count ? {name: 'AtlasCategoryView', params: {id: category._id}} : ''"
                    class="text-decoration-none"
                    :class="{'disabled-link': !category.images_count}"
                >
                    <div v-if="hovered !== category._id">
                        <div class="document-icon-wrapper d-inline-flex justify-content-center align-items-center mb-2 w-full">
                            
                            <div class="icon">
                                <img 
                                    v-if="category.thumbnail_url" 
                                    :src="getImageUrl(category.thumbnail_url)" 
                                    :alt="category.name"
                                    class="category-thumbnail"
                                >
                                <i v-else class="pi pi-image"></i>
                            </div>
                            
                        </div>
                        <div class="document-name text-center">
                            {{ category.name }}
                            <div class="image-counter">
                                {{ category.images_count }} фото
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="open-button" v-if="category.images_count">Просмотреть</div>
                        <div class="open-button disabled" v-else>Нет фото</div>
                    </div>
                </router-link>
            </div>
        </div>
    </div>

    <!-- Категории с дочерними элементами -->
    <div v-for="category in parentCategories" :key="category._id" class="parent-category mb-4">
        <div class="parent-card">
            <div class="d-flex align-items-start p-4">
                <div class="parent-image-wrapper mr-4">
                    <img 
                        v-if="category.thumbnail_url" 
                        :src="getImageUrl(category.thumbnail_url)" 
                        :alt="category.name"
                        class="parent-thumbnail"
                    >
                    <i v-else class="pi pi-image placeholder-icon"></i>
                </div>
                <div class="flex-grow-1">
                    <div class="d-flex align-items-center flex-wrap">
                        <h3 class="mb-0 mr-2">{{ category.name }}</h3>
                        <div class="image-counter">
                            {{ category.images_count }} фото
                        </div>
                    </div>
                    <p v-if="category.description" class="description mt-2 mb-2">
                        {{ category.description }}
                    </p>
                    <div class="mt-4">
                        <router-link 
                            v-if="category.images_count"
                            :to="{name: 'AtlasCategoryView', params: {id: category._id}}"
                            class="view-link"
                        >
                            Просмотреть
                        </router-link>
                    </div>
                </div>
            </div>

            <!-- Дочерние категории -->
            <div class="children-container" v-if="getChildCategories(category._id).length">
                <div class="viewers-list flex flex-wrap gap-3">
                    <div 
                        v-for="child in getChildCategories(category._id)" 
                        :key="child._id"
                        class="document child-document"
                        @mouseover="hover(child._id)" 
                        @mouseleave="leave"
                    >
                        <router-link 
                            :to="child.images_count ? {name: 'AtlasCategoryView', params: {id: child._id}} : ''"
                            class="text-decoration-none"
                            :class="{'disabled-link': !child.images_count}"
                        >
                            <div v-if="hovered !== child._id">
                                <div class="document-icon-wrapper mb-2">
                                    <div class="icon">
                                        <img 
                                            v-if="child.thumbnail_url" 
                                            :src="getImageUrl(child.thumbnail_url)" 
                                            :alt="child.name"
                                            class="category-thumbnail"
                                        >
                                        <i v-else class="pi pi-image"></i>
                                    </div>
                                </div>
                                <div class="document-name text-center">
                                    {{ child.name }}
                                    <div class="image-counter" v-if="child.images_count">
                                        {{ child.images_count }} фото
                                    </div>
                                </div>
                            </div>
                            <div v-else>
                                <div class="open-button" v-if="child.images_count">Просмотреть</div>
                                <div class="open-button disabled" v-else>Нет фото</div>
                            </div>
                        </router-link>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
    categories: {
        type: Array,
        required: true
    }
});

const hovered = ref(null);

// Получаем только категории без дочерних элементов
const simpleCategories = computed(() => {
    return props.categories.filter(cat => 
        !cat.parent_id && !getChildCategories(cat._id).length
    );
});

// Получаем родительские категории с дочерними элементами
const parentCategories = computed(() => {
    return props.categories.filter(cat => 
        !cat.parent_id && getChildCategories(cat._id).length > 0
    );
});

const getChildCategories = (parentId) => {
    return props.categories.filter(cat => cat.parent_id === parentId);
};

const getImageUrl = (path) => {
    return path ? `https://api.labhub.online${path}` : null;
};

const hover = (id) => {
    hovered.value = id;
};

const leave = () => {
    hovered.value = null;
};
</script>

<style scoped>
.btn-enter {
justify-content: center;
align-items: center;
gap: 10px;
flex-shrink: 0;
border-radius: 100px;
background: #3fe7de;
color: #ffffff;
}

.viewers-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 20px;
}

.document {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 300px;
    height: 200px;
    padding: 20px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
}

.document:hover {
    background-color: #e8f8ff;
    transform: translateY(-2px);
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
}

.child-document {
    height: 180px;
}

.document-icon-wrapper {
    transition: opacity 0.3s ease;
}

.category-thumbnail {
    width: 100%;
    height: 120px;
    object-fit: contain;
}

.parent-image-wrapper {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
}

.parent-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.placeholder-icon {
    font-size: 2rem;
    color: #ccc;
}

.document-name {
    color: #5f5f5f;
    font-family: Nunito;
    font-size: 14px;
    font-weight: 500;
    margin-top: 15px;
    transition: opacity 0.3s ease;
    text-align: center;
}

.image-counter {
    display: inline-block;
    background: var(--primary-light, #e3f2fd);
    color: var(--primary, #1384c5);
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.8rem;
    margin-top: 4px;
}

.parent-card {
    background: white;
    border-radius: 10px;
    box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.1);
    overflow: hidden;
}

.children-container {
    padding: 20px;
    background: #f8f9fa;
    border-top: 1px solid #eee;
}

.description {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
    line-height: 1.4;
}

.view-link {
    display: inline-flex;
    align-items: center;
    padding: 6px 16px;
    background: #3fe7de;
    color: white !important;
    border-radius: 4px;
    text-decoration: none;
    font-weight: 500;
    transition: background-color 0.2s;
}

.view-link:hover {
    background: #20b1aa;
}

.open-button {
    background: #3fe7de;
    color: white;
    padding: 8px 20px;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.open-button:hover {
    background: #20b1aa;
}

.open-button.disabled {
    background-color: #ccc;
    cursor: not-allowed;
}

.disabled-link {
    pointer-events: none;
    opacity: 0.6;
}

@media (min-width: 1200px) {
    .document {
        flex: 1 1 calc(33.333% - 20px);
    }
}

@media (min-width: 768px) and (max-width: 1199px) {
    .document {
        flex: 1 1 calc(50% - 20px);
    }
}

@media (max-width: 767px) {
    .document {
        flex: 1 1 100%;
    }
    
    .parent-card {
        flex-direction: column;
    }
    
    .parent-image-wrapper {
        width: 100%;
        margin-bottom: 1rem;
    }
    
    .children-container {
        padding: 10px;
    }
}
</style>